<script lang="ts" setup>
import { PropType, computed, onBeforeUnmount, ref } from "vue";
import { gql } from "@urql/core";
import { useMutation } from "@urql/vue";
import { DocumentSpaceItem, type DocumentSpace } from "@/graphql/operations";
import { Dropdown } from "@/components";
import {
  buildClipboardDocumentTitle,
  buildDocumentClipboardPayload,
  buildDocumentLink,
  copyClipboardPayload,
  Formatter,
  getStorageProxyUrl,
  isSharedSpaceConsentGateError,
  MimeType,
  shouldUseAppHostForDocumentLinks,
  type SharedSpaceConsentFailureReason,
} from "@/util";
import { useDocumentSpaceSharing } from "@/composables";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import ManualSharingConsentModal from "./ManualSharingConsentModal.vue";
import { useManualSharingConsent } from "@/composables/useManualSharingConsent";
import { useManualSharingConfirmation } from "@/composables/useManualSharingConfirmation";
import ConsentCheckModal from "./ConsentCheckModal.vue";
import MissingConsentModal from "./MissingConsentModal.vue";
import type {
  DeleteReconciledItem,
  IsItemDeletionPending,
} from "@/composables/useReconciledItemDeletion";

const props = defineProps({
  documents: {
    type: Array as PropType<Array<DocumentSpaceItem>>,
    required: true,
  },
  documentSpaceId: {
    type: String,
    required: true,
  },
  documentSpace: {
    type: Object as PropType<DocumentSpace>,
    required: true,
  },
  emailVerificationValid: {
    type: Boolean,
    required: true,
  },
  deleteItem: {
    type: Function as PropType<DeleteReconciledItem>,
    required: true,
  },
  isItemDeletionPending: {
    type: Function as PropType<IsItemDeletionPending>,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const user = store.getters.user;

type DeleteDocumentSpaceItemByIdMutation = {
  deleteDocumentSpaceItem?: {
    clientMutationId?: string | null;
  } | null;
};

type DeleteDocumentSpaceItemByIdVariables = {
  id: string;
};

const DeleteDocumentSpaceItemByIdDocument = gql`
  mutation DeleteDocumentSpaceItemById($id: BigInt!) {
    deleteDocumentSpaceItem(input: { id: $id }) {
      clientMutationId
    }
  }
`;

const { executeMutation: deleteDocumentSpaceItemById } = useMutation<
  DeleteDocumentSpaceItemByIdMutation,
  DeleteDocumentSpaceItemByIdVariables
>(DeleteDocumentSpaceItemByIdDocument);

const handleAction = async (
  action: string,
  documentSpaceItem: DocumentSpaceItem,
) => {
  switch (action) {
    case "remove":
      await removeItem(documentSpaceItem.id);
      break;

    case "copy_doc_link":
      await copyItemLink(documentSpaceItem.id, documentSpaceItem);
      break;
  }
};

const removeItem = async (itemId: string) => {
  await props.deleteItem(itemId, () =>
    deleteDocumentSpaceItemById({
      id: itemId,
    }),
  );
};

const dropdownRefs = new Map<string, HTMLElement>();
const tooltipTimeouts = new Map<string, number>();

onBeforeUnmount(() => {
  tooltipTimeouts.forEach((timeout) => window.clearTimeout(timeout));
  tooltipTimeouts.clear();
  dropdownRefs.clear();
});

const setDropdownWrapper = (spaceItemId: string, el: HTMLElement | null) => {
  if (el) {
    dropdownRefs.set(spaceItemId, el);
    return;
  }

  dropdownRefs.delete(spaceItemId);
};

const showCopyTooltip = (spaceItemId: string, message: string) => {
  const wrapper = dropdownRefs.get(spaceItemId);
  if (!wrapper) {
    return;
  }

  wrapper.setAttribute("data-tooltip", message);
  const existingTimeout = tooltipTimeouts.get(spaceItemId);
  if (existingTimeout) {
    window.clearTimeout(existingTimeout);
  }

  const timeout = window.setTimeout(() => {
    wrapper.removeAttribute("data-tooltip");
    tooltipTimeouts.delete(spaceItemId);
  }, 1000);

  tooltipTimeouts.set(spaceItemId, timeout);
};

const itemTitle = (item: DocumentSpaceItem) => {
  return (
    item.document?.displayTitle ??
    item.flow?.title ??
    t("documentSpace.documents.title")
  );
};

const copyItemLink = async (
  spaceItemId?: string | null,
  item?: DocumentSpaceItem | null,
) => {
  if (!spaceItemId) {
    return;
  }

  const document = item?.document;
  const advanced = user.hasAdvancedClipboard();
  const urlPrefix = sharedSpaceUrlPrefix.value;
  const fallbackPrefix = `${window.location.origin}/2/`;
  const useAppHostForDocuments = shouldUseAppHostForDocumentLinks({
    urlPrefix,
    forcePitchviewPrefix: user.forcesPitchviewPrefixForDocLinks(),
  });

  // Dropdown choices disappear on selection; restore to their stable trigger.
  dropdownRefs
    .get(spaceItemId)
    ?.querySelector<HTMLElement>(".dropdown_trigger")
    ?.focus();
  return requestCopy({
    persist: confirmation.forTarget({
      documentSpaceId: props.documentSpace.id,
      expectedCrmContactId:
        props.documentSpace.documentSpaceCrmMapping?.crmContact?.id ?? null,
      documentSpaceItemId: spaceItemId,
    }),
    prepare: async (signal) => {
      const urlSegment = await generateVoucherSegment(false, undefined, signal);
      signal.throwIfAborted();
      const documentLink = buildDocumentLink({
        urlPrefix,
        urlSegment,
        spaceItemId,
        fallbackPrefix,
        useAppHostForDocuments,
      });
      if (!advanced) return () => navigator.clipboard.writeText(documentLink);
      const payload = buildDocumentClipboardPayload({
        documentLink,
        documentTitle: document?.displayTitle
          ? buildClipboardDocumentTitle({
              displayTitle: document.displayTitle,
              mimeType: document.mimeType,
              originalMimeType: document.originalMimeType,
              typeLabels: {
                video: t("documentSpace.copyLink.typeLabels.video"),
                image: t("documentSpace.copyLink.typeLabels.image"),
              },
            })
          : item
            ? itemTitle(item)
            : t("documentSpace.documents.title"),
      });
      return () => copyClipboardPayload(payload, signal);
    },
    onSuccess: () =>
      showCopyTooltip(spaceItemId, t("documentSpace.copyLink.tooltip")),
    onError: (err, phase) => {
      if (isSharedSpaceConsentGateError(err)) {
        showMissingConsentModal(err.reason);
        return true;
      }
      if (!user.hasRequiredManualConsentForSharing())
        showCopyTooltip(
          spaceItemId,
          phase === "copying"
            ? t("documentSpace.copyLink.tooltipErrorCopy")
            : t("documentSpace.copyLink.tooltipErrorGenerate"),
        );
      return false;
    },
  });
};

const sortedDocuments = computed(() => {
  return props.documents;
});

const flowPresenterRoute = (flowId: string) => ({
  name: "ViewSharedSpaceCustomFlowPageByPage",
  params: { spaceId: props.documentSpaceId, id: flowId },
});

const documentSpaceRef = computed(() => props.documentSpace);
const emailVerificationValidRef = computed(() => props.emailVerificationValid);
const {
  displayShareOptions,
  checkingConsent,
  sharedSpaceUrlPrefix,
  generateVoucherSegment,
} = useDocumentSpaceSharing({
  documentSpace: documentSpaceRef,
  emailVerificationValid: emailVerificationValidRef,
});
const confirmation = useManualSharingConfirmation();
const {
  active: manualConsentActive,
  phase: manualConsentPhase,
  retry: retryCopy,
  request: requestCopy,
  confirm: confirmCopy,
  cancel: cancelCopy,
} = useManualSharingConsent({
  required: () => user.hasRequiredManualConsentForSharing(),
  eligible: () => displayShareOptions.value,
  context: () =>
    JSON.stringify([
      props.documentSpace.id,
      props.documentSpace.allowedEmailAddresses,
      props.documentSpace.config?.requiresEmailAuthentication,
      props.documentSpace.documentSpaceCrmMapping?.crmContact,
      props.documents,
      sharedSpaceUrlPrefix.value,
      user.mergedConfig,
      user.hasAdvancedClipboard(),
      confirmation.locale.value,
    ]),
});
const missingConsentModalActive = ref(false);
const consentFailureReason = ref<SharedSpaceConsentFailureReason>("missing");

const showMissingConsentModal = (reason: SharedSpaceConsentFailureReason) => {
  consentFailureReason.value = reason;
  missingConsentModalActive.value = true;
};

type ContextMenuItem = {
  value: string;
  title: string;
  destructive?: boolean;
  disabled?: boolean;
};

const contextMenuItemsFor = (
  item: DocumentSpaceItem,
): Array<ContextMenuItem> => {
  const items: Array<ContextMenuItem> = [
    {
      value: "copy_doc_link",
      title: item.flow
        ? t("documentSpace.copyLink.buttonText")
        : t("documentSpace.document.copyLink"),
      disabled: !displayShareOptions.value,
    },
    {
      value: "remove",
      title: t("documentSpace.document.remove"),
      destructive: true,
      disabled: props.isItemDeletionPending(item.id),
    },
  ];

  return items;
};
</script>

<template>
  <div class="document_list" :data-share-options-enabled="displayShareOptions">
    <li
      v-for="(item, idx) in sortedDocuments"
      class="row"
      :key="item.id"
      :data-id="item.id"
      :data-idx="idx"
      :data-index="idx"
    >
      <div class="cell thumbnail_container">
        <router-link
          v-if="item.document"
          :to="{
            name: 'StackedProjector',
            params: { workspaceId: item.document.workspaceId },
            query: { fid: item.document.firestoreId },
          }"
          target="_blank"
          draggable="false"
        >
          <img
            class="thumbnail_image"
            :src="getStorageProxyUrl(item.document.thumbnailDownloadUrl)"
          />
        </router-link>

        <router-link
          v-else-if="item.flow"
          :to="flowPresenterRoute(item.flow.id)"
          target="_blank"
          draggable="false"
        >
          <img
            v-if="item.flow.thumbnailUrl"
            class="thumbnail_image"
            :src="getStorageProxyUrl(item.flow.thumbnailUrl)"
          />
          <div v-else class="flow_thumbnail_placeholder">
            <span class="material-symbols-outlined">dynamic_feed</span>
          </div>
        </router-link>
      </div>

      <div class="cell grow center">
        <router-link
          v-if="item.document"
          :to="{
            name: 'StackedProjector',
            params: { workspaceId: item.document.workspaceId },
            query: { fid: item.document.firestoreId },
          }"
          target="_blank"
          draggable="false"
        >
          <div>
            {{ item.document.displayTitle }}

            <span
              class="badge error"
              v-if="item.document.documentExpiration?.expiredAt"
            >
              {{ $t("documentSpace.document.expired") }}
            </span>
          </div>
          <div class="info">
            <template v-if="MimeType.isVideo(item.document.mimeType)">
              <span>{{ Formatter.formatDuration(item.document.pages) }}</span>
            </template>
            <template v-else>
              <span>{{
                $t("documentSearch.document.numPages", {
                  count: item.document.pages,
                })
              }}</span>
            </template>
            <span> - </span>
            <span>
              {{
                MimeType.getName(
                  item.document.originalMimeType ?? item.document.mimeType,
                )
              }}
            </span>
          </div>
        </router-link>

        <router-link
          v-else-if="item.flow"
          :to="flowPresenterRoute(item.flow.id)"
          target="_blank"
          draggable="false"
        >
          <div>
            {{ item.flow.title }}
          </div>
          <div class="info">
            <span>{{
              $t("documentSearch.document.numPages", {
                count: item.flow.flowItems?.totalCount ?? 0,
              })
            }}</span>
            <span> - </span>
            <span>{{ $t("customFlows.itemType") }}</span>
          </div>
        </router-link>
      </div>

      <div class="cell center">
        <div
          class="dropdown_wrapper"
          :ref="(el) => setDropdownWrapper(item.id, el as HTMLElement | null)"
        >
          <Dropdown
            class="right"
            :items="contextMenuItemsFor(item)"
            @change="(action: string) => handleAction(action, item)"
          >
            <span class="material-symbols-outlined">more_vert</span>
          </Dropdown>
        </div>
      </div>
    </li>
  </div>

  <ManualSharingConsentModal
    v-if="manualConsentActive"
    :phase="manualConsentPhase"
    @retry="retryCopy"
    @confirm="confirmCopy"
    @close="cancelCopy"
  />

  <ConsentCheckModal v-if="checkingConsent" />

  <MissingConsentModal
    v-if="missingConsentModalActive"
    :reason="consentFailureReason"
    @close="missingConsentModalActive = false"
  />
</template>

<style scoped lang="scss">
a {
  text-decoration: none;
  color: inherit;
}

.fade-move,
.fade-enter-active,
.fade-leave-active {
  transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: scaleY(0.01) translate(30px, 0);
}

.fade-leave-active {
  position: absolute;
}

.thumbnail_container {
  height: 60px;
  width: 60px;
  margin-right: 0.5em;
}

.thumbnail_container a {
  height: 100%;
  width: 100%;
  background-color: var(--classic-media-surface, #fafafa);
  display: inline-block;
}

.draggable img.thumbnail_image {
  object-fit: contain;
  border-radius: 4%;
  background-color: var(--classic-media-surface, #fafafa);
  height: 100%;
  width: 100%;
}

.flow_thumbnail_placeholder {
  align-items: center;
  background: var(--classic-surface-raised, #f2f7f8);
  color: var(--classic-accent, $color-secondary);
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
}

.dropdown_wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  width: fit-content;
  height: fit-content;
  align-self: center;
}

.dropdown_wrapper[data-tooltip]::before {
  background: var(--classic-tooltip-surface, rgba(74, 74, 74, 0.9));
  border-radius: 2px;
  content: attr(data-tooltip);
  padding: 0.5rem 1rem;
  text-overflow: ellipsis;
  white-space: pre;
  right: -10%;
  top: 100%;
  margin-top: 4px;
  position: absolute;
}

.dropdown_wrapper[data-tooltip]::before,
.dropdown_wrapper[data-tooltip]::after {
  box-sizing: border-box;
  color: #fff;
  display: inline-block;
  font-size: 0.75rem;
  pointer-events: none;
  position: absolute;
  z-index: 20;
}

.dropdown_wrapper[data-tooltip]::after {
  content: "";
  border-style: solid;
  border-width: 0 6px 6px 6px;
  border-color: transparent transparent
    var(--classic-tooltip-surface, rgba(74, 74, 74, 0.9)) transparent;
  left: 50%;
  top: calc(100% - 2px);
  transform: translateX(-50%);
}

.dropdown_wrapper[data-tooltip] {
  overflow: visible;
}

.info {
  color: var(--classic-text-secondary, rgba(82, 74, 62, 0.82));
  font-size: 0.9em;
}

.badge {
  background-color: var(--classic-surface-raised, #f9f9f9);
  font-size: 0.85em;
  padding: 0 10px;
  line-height: 1.8em;
  font-weight: 500;
  margin-bottom: 5px;
}

.badge.error {
  background: var(--classic-danger, $color-error);
  color: var(--classic-on-danger, #fff);
}
</style>
